import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { 
  Users, UserPlus, Calendar, Briefcase, TrendingUp, TrendingDown,
  CheckCircle, XCircle, Clock, FileText, AlertCircle, Building,
  Target, Award, DollarSign, Activity, ArrowRight, Search,
  UserCheck, UserX, CalendarDays, ClipboardList, GraduationCap,
  BadgeCheck, Hourglass, PieChart, BarChart3
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function HRDashboard({ user, onLogout }) {
  const [stats, setStats] = useState({
    employees: { total: 0, active: 0, onLeave: 0, newThisMonth: 0 },
    recruitment: { openJobs: 0, candidates: 0, interviews: 0, offers: 0, hired: 0 },
    attendance: { present: 0, absent: 0, late: 0, percentage: 0 },
    leaves: { pending: 0, approved: 0, onLeaveToday: 0 },
    departments: 0,
    branches: 0
  });
  const [loading, setLoading] = useState(true);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [recentHires, setRecentHires] = useState([]);
  const [upcomingHolidays, setUpcomingHolidays] = useState([]);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [dashboardRes, employeesRes, candidatesRes, leavesRes, holidaysRes, approvalsRes] = await Promise.all([
        axios.get(`${API}/api/dashboard/stats`, { headers }).catch(() => ({ data: {} })),
        axios.get(`${API}/api/employees?limit=5`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/api/recruitment/candidates?limit=10`, { headers }).catch(() => ({ data: { candidates: [], count: 0 } })),
        axios.get(`${API}/api/leaves?status=Pending`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/api/holidays/upcoming?limit=5`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/api/approvals/pending?limit=5`, { headers }).catch(() => ({ data: [] }))
      ]);

      const d = dashboardRes.data;
      setStats({
        employees: {
          total: d.total_employees || 0,
          active: d.total_employees || 0,
          onLeave: d.on_leave_today || 0,
          newThisMonth: d.new_hires_this_month || 0
        },
        recruitment: {
          openJobs: d.open_positions || 0,
          candidates: d.total_candidates || candidatesRes.data?.count || 0,
          interviews: 0,
          offers: 0,
          hired: d.new_hires_this_month || 0
        },
        attendance: {
          present: d.present_today || 0,
          absent: d.absent_today || 0,
          late: 0,
          percentage: d.attendance_percentage || 0
        },
        leaves: {
          pending: d.pending_leaves || leavesRes.data?.length || 0,
          approved: 0,
          onLeaveToday: d.on_leave_today || 0
        },
        departments: d.total_departments || 0,
        branches: 0
      });

      // Recent hires from employees
      const employees = Array.isArray(employeesRes.data) ? employeesRes.data : [];
      setRecentHires(employees.slice(0, 5).map(e => ({
        name: `${e.first_name || ''} ${e.last_name || ''}`.trim() || 'N/A',
        department: e.department || e.departments?.[0] || 'General',
        joinDate: e.joining_date || e.created_at?.split('T')[0] || 'Recent',
        status: e.employee_status || 'Active'
      })));

      // Upcoming holidays
      setUpcomingHolidays(Array.isArray(holidaysRes.data) ? holidaysRes.data.slice(0, 5) : []);

      // Pending approvals
      const leaves = Array.isArray(leavesRes.data) ? leavesRes.data : [];
      setPendingApprovals(leaves.slice(0, 5).map(l => ({
        type: 'Leave',
        employee: l.employee_name || l.employee_id,
        description: `${l.leave_type} - ${l.number_of_days} days`,
        id: l.id
      })));

    } catch (error) {
      console.error('Failed to fetch HR dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, subtitle, icon: Icon, bgColor, iconBg, link, trend, trendValue }) => (
    <Link to={link || '#'} className="bg-white rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 p-5 border border-gray-100">
      <div className="flex items-center gap-4">
        <div className={`p-3 rounded-xl ${iconBg}`}>
          <Icon className="w-6 h-6" style={{ color: bgColor }} />
        </div>
        <div className="flex-1">
          <p className="text-sm text-gray-500 mb-0.5">{title}</p>
          <div className="flex items-baseline gap-2">
            <h3 className="text-2xl font-bold" style={{ color: '#1a1f3a' }}>{value}</h3>
            {trend && (
              <span className={`text-xs font-medium flex items-center ${trend === 'up' ? 'text-green-600' : 'text-red-500'}`}>
                {trend === 'up' ? <TrendingUp className="w-3 h-3 mr-0.5" /> : <TrendingDown className="w-3 h-3 mr-0.5" />}
                {trendValue}
              </span>
            )}
          </div>
          <p className="text-xs text-gray-400">{subtitle}</p>
        </div>
      </div>
    </Link>
  );

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: '#2b3380' }}></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6 bg-gray-50 min-h-screen">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold" style={{ color: '#1a1f3a' }}>HR Dashboard</h1>
          <p className="text-gray-500">Welcome back, {user?.first_name || 'HR Manager'}</p>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <StatCard
            title="Total Employees"
            value={stats.employees.total}
            subtitle={`${stats.employees.newThisMonth} new this month`}
            icon={Users}
            bgColor="#2b3380"
            iconBg="bg-indigo-50"
            link="/employees"
            trend="up"
            trendValue="+5%"
          />
          <StatCard
            title="Present Today"
            value={stats.attendance.present}
            subtitle={`${stats.attendance.percentage}% attendance rate`}
            icon={UserCheck}
            bgColor="#4caf50"
            iconBg="bg-green-50"
            link="/attendance"
          />
          <StatCard
            title="On Leave"
            value={stats.leaves.onLeaveToday}
            subtitle={`${stats.leaves.pending} pending requests`}
            icon={CalendarDays}
            bgColor="#ff9800"
            iconBg="bg-orange-50"
            link="/leaves"
          />
          <StatCard
            title="Open Positions"
            value={stats.recruitment.openJobs}
            subtitle={`${stats.recruitment.candidates} candidates`}
            icon={Briefcase}
            bgColor="#7e57c2"
            iconBg="bg-purple-50"
            link="/recruitment/jobs"
          />
        </div>

        {/* HR Activities Section */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          {/* Recruitment Pipeline */}
          <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-gray-800">Recruitment Pipeline</h2>
              <Link to="/recruitment/candidates" className="text-sm text-indigo-600 hover:underline flex items-center gap-1">
                View All <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <Search className="w-5 h-5 text-blue-600" />
                  <span className="text-sm font-medium text-gray-700">Screening</span>
                </div>
                <span className="text-lg font-bold text-blue-600">{stats.recruitment.candidates}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <ClipboardList className="w-5 h-5 text-purple-600" />
                  <span className="text-sm font-medium text-gray-700">Interviews</span>
                </div>
                <span className="text-lg font-bold text-purple-600">{stats.recruitment.interviews}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-orange-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <FileText className="w-5 h-5 text-orange-600" />
                  <span className="text-sm font-medium text-gray-700">Offers Pending</span>
                </div>
                <span className="text-lg font-bold text-orange-600">{stats.recruitment.offers}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <BadgeCheck className="w-5 h-5 text-green-600" />
                  <span className="text-sm font-medium text-gray-700">Hired</span>
                </div>
                <span className="text-lg font-bold text-green-600">{stats.recruitment.hired}</span>
              </div>
            </div>
          </div>

          {/* Pending Approvals */}
          <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-gray-800">Pending Approvals</h2>
              <Link to="/approvals" className="text-sm text-indigo-600 hover:underline flex items-center gap-1">
                View All <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            {pendingApprovals.length > 0 ? (
              <div className="space-y-3">
                {pendingApprovals.map((item, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                    <div>
                      <p className="text-sm font-medium text-gray-800">{item.employee}</p>
                      <p className="text-xs text-gray-500">{item.description}</p>
                    </div>
                    <span className="px-2 py-1 text-xs font-medium bg-amber-100 text-amber-700 rounded-full">
                      {item.type}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-8 text-gray-400">
                <CheckCircle className="w-12 h-12 mb-2" />
                <p className="text-sm">No pending approvals</p>
              </div>
            )}
          </div>

          {/* Upcoming Holidays */}
          <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-gray-800">Upcoming Holidays</h2>
              <Link to="/holiday-calendar" className="text-sm text-indigo-600 hover:underline flex items-center gap-1">
                View All <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            {upcomingHolidays.length > 0 ? (
              <div className="space-y-3">
                {upcomingHolidays.map((holiday, idx) => (
                  <div key={idx} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <div className="w-12 h-12 flex flex-col items-center justify-center bg-indigo-100 rounded-lg">
                      <span className="text-xs font-medium text-indigo-600">
                        {new Date(holiday.date).toLocaleDateString('en', { month: 'short' })}
                      </span>
                      <span className="text-lg font-bold text-indigo-700">
                        {new Date(holiday.date).getDate()}
                      </span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-800">{holiday.name}</p>
                      <p className="text-xs text-gray-500">{holiday.type || 'Public Holiday'}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-8 text-gray-400">
                <Calendar className="w-12 h-12 mb-2" />
                <p className="text-sm">No upcoming holidays</p>
              </div>
            )}
          </div>
        </div>

        {/* Quick Actions & Recent Hires */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Quick Actions */}
          <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-100">
            <h2 className="font-semibold text-gray-800 mb-4">Quick Actions</h2>
            <div className="grid grid-cols-2 gap-3">
              <Link to="/onboarding/new" className="flex items-center gap-3 p-4 bg-indigo-50 rounded-xl hover:bg-indigo-100 transition-colors">
                <UserPlus className="w-6 h-6 text-indigo-600" />
                <span className="text-sm font-medium text-gray-700">Add Employee</span>
              </Link>
              <Link to="/recruitment/jobs" className="flex items-center gap-3 p-4 bg-purple-50 rounded-xl hover:bg-purple-100 transition-colors">
                <Briefcase className="w-6 h-6 text-purple-600" />
                <span className="text-sm font-medium text-gray-700">Post Job</span>
              </Link>
              <Link to="/leave-balance-allocation" className="flex items-center gap-3 p-4 bg-orange-50 rounded-xl hover:bg-orange-100 transition-colors">
                <CalendarDays className="w-6 h-6 text-orange-600" />
                <span className="text-sm font-medium text-gray-700">Manage Leaves</span>
              </Link>
              <Link to="/payroll" className="flex items-center gap-3 p-4 bg-green-50 rounded-xl hover:bg-green-100 transition-colors">
                <DollarSign className="w-6 h-6 text-green-600" />
                <span className="text-sm font-medium text-gray-700">Run Payroll</span>
              </Link>
              <Link to="/hr-settings" className="flex items-center gap-3 p-4 bg-teal-50 rounded-xl hover:bg-teal-100 transition-colors">
                <Building className="w-6 h-6 text-teal-600" />
                <span className="text-sm font-medium text-gray-700">HR Settings</span>
              </Link>
              <Link to="/roles" className="flex items-center gap-3 p-4 bg-blue-50 rounded-xl hover:bg-blue-100 transition-colors">
                <Award className="w-6 h-6 text-blue-600" />
                <span className="text-sm font-medium text-gray-700">Manage Roles</span>
              </Link>
            </div>
          </div>

          {/* Recent Hires */}
          <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-gray-800">Recent Hires</h2>
              <Link to="/employees" className="text-sm text-indigo-600 hover:underline flex items-center gap-1">
                View All <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            {recentHires.length > 0 ? (
              <div className="space-y-3">
                {recentHires.map((hire, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-medium" style={{ backgroundColor: '#2b3380' }}>
                        {hire.name.charAt(0)}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-800">{hire.name}</p>
                        <p className="text-xs text-gray-500">{hire.department}</p>
                      </div>
                    </div>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                      hire.status === 'Active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {hire.status}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-8 text-gray-400">
                <Users className="w-12 h-12 mb-2" />
                <p className="text-sm">No recent hires</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default HRDashboard;
